import { useFormik } from 'formik';
import { Filter, RefreshCw, Search } from 'lucide-react';
import styled from 'styled-components';

import { Button } from '../../../components/atoms/button/button';
import { Input } from '../../../components/atoms/input/input';
import { Select } from '../../../components/atoms/select/select';
import {
  ClientesFilters,
  STATUS_KYC_LABEL,
  StatusKycCliente,
  TipoPessoaCliente,
} from '../../../types/cliente';

interface Props {
  initialValues: ClientesFilters;
  onApply: (values: ClientesFilters) => void;
  onClear: () => void;
}

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 18px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const tipoPessoaOptions = [
  { label: 'Todos', value: '' },
  { label: 'Pessoa Física', value: 'PF' },
  { label: 'Pessoa Jurídica', value: 'PJ' },
];

const statusKycOptions = [
  { label: 'Todos os status', value: '' },
  ...Object.entries(STATUS_KYC_LABEL).map(([value, label]) => ({
    label,
    value,
  })),
];

export const ClientesFiltersBar = ({ initialValues, onApply, onClear }: Props) => {
  const formik = useFormik<ClientesFilters>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => onApply(values),
  });

  return (
    <Wrapper
      as="form"
      onSubmit={formik.handleSubmit}
      aria-label="Filtros de clientes"
    >
      <Field>
        Busca (nome ou e-mail)
        <Input
          name="search"
          placeholder="Ex: Alice ou alice@cliente.com"
          value={formik.values.search ?? ''}
          onChange={formik.handleChange}
          startIcon={<Search size={16} />}
        />
      </Field>

      <Field>
        Documento
        <Input
          name="documento"
          placeholder="CPF ou CNPJ"
          value={formik.values.documento ?? ''}
          onChange={formik.handleChange}
        />
      </Field>

      <Field>
        Tipo
        <Select
          options={tipoPessoaOptions}
          name="tipoPessoa"
          value={formik.values.tipoPessoa ?? ''}
          onChange={(e) =>
            formik.setFieldValue(
              'tipoPessoa',
              (e.target.value || '') as TipoPessoaCliente | '',
            )
          }
        />
      </Field>

      <Field>
        Status KYC
        <Select
          options={statusKycOptions}
          name="statusKyc"
          value={(formik.values.statusKyc ?? '').toString()}
          onChange={(e) =>
            formik.setFieldValue(
              'statusKyc',
              e.target.value === ''
                ? ''
                : (Number(e.target.value) as StatusKycCliente),
            )
          }
        />
      </Field>

      <Actions>
        <Button
          variant="ghost"
          type="button"
          startIcon={<RefreshCw size={14} />}
          onClick={() => {
            formik.resetForm({ values: {} });
            onClear();
          }}
        >
          Limpar
        </Button>
        <Button
          variant="primary"
          type="submit"
          startIcon={<Filter size={14} />}
        >
          Aplicar filtros
        </Button>
      </Actions>
    </Wrapper>
  );
};
